import { apiClient } from '../config/ApiClient';
import type { StoreDTO } from '../dto/StoreDTO';
import type { OrderItemStatusUpdateRequestDTO } from '../dto/order/OrderItemStatusUpdateRequestDTO';
import { ItemFeedResponse } from '../dto/item/ItemFeedResponse';
import {
  StoreOrderDTO,
  StoreOrderItemStatus,
} from '../dto/order/StoreOrderDTO';

export interface StoreItemSalesDTO {
  itemId: string;
  itemName: string;
  unitsSold: number;
  totalSold: number;
  collectableAmount: number;
  collectedAmount: number;
  pendingAmount: number;
}

export interface StoreDashboardDTO {
  storeId: string;
  startDate: string;
  endDate: string;
  totalSold: number;
  totalCollectable: number;
  totalShippingToPay: number;
  totalCollected: number;
  totalPendingCollection: number;
  estimatedNextPayment: number;
  itemSales: StoreItemSalesDTO[];
}

export const fetchStores = () => apiClient.get<StoreDTO[]>('/store/public');

export const fetchStoresByCity = (city: string) =>
  apiClient.get<StoreDTO[]>(`/store/public/city/${encodeURIComponent(city)}`);

export const fetchStoreById = (id: string) =>
  apiClient.get<StoreDTO>(`/store/public/${id}`);

/**
 * GET /store/public/items
 * Query params: storeId (required), category?, cursor? | page?, colors[]?, sizes[]?
 */
export type StoreItemsParams = {
  storeId: string; // required
  category?: string;
  cursor?: string; // mutually exclusive with page
  colors?: string[];
  sizes?: string[];
  page?: number; // 1-based
};

export const fetchStoreItems = (params: StoreItemsParams) => {
  const sp = new URLSearchParams();

  // required
  sp.set('storeId', params.storeId);

  // optional scalars
  if (params.category?.trim()) sp.set('category', params.category.trim());
  if (params.cursor?.trim()) sp.set('cursor', params.cursor.trim());
  if (typeof params.page === 'number') sp.set('page', String(params.page));

  // arrays as repeated params
  params.colors?.forEach((c) => {
    const v = c?.trim();
    if (v) sp.append('colors', v);
  });
  params.sizes?.forEach((s) => {
    const v = s?.trim();
    if (v) sp.append('sizes', v);
  });

  return apiClient.get<ItemFeedResponse>('/store/public/items', { params: sp });
};

export const updateStore = (id: string, dto: StoreDTO) =>
  apiClient.put<StoreDTO>(`/store/${id}`, dto);

export const deleteStore = (id: string) =>
  apiClient.delete<void>(`/store/${id}`);

/** POST /store/orders/accept */
export const acceptOrderItem = (storeOrderId: string, orderItemId: string) =>
  apiClient.post<string>('/store/orders/accept', {
    storeOrderId,
    orderItemId,
    newStatus: StoreOrderItemStatus.ACCEPTED,
  } satisfies OrderItemStatusUpdateRequestDTO);

/** POST /store/orders/reject */
export const rejectOrderItem = (storeOrderId: string, orderItemId: string) =>
  apiClient.post<string>('/store/orders/reject', {
    storeOrderId,
    orderItemId,
    newStatus: StoreOrderItemStatus.REJECTED_REFUND_IN_PROGRESS,
  } satisfies OrderItemStatusUpdateRequestDTO);

/** GET /store/orders */
export const fetchStoreOrders = () =>
  apiClient.get<StoreOrderDTO[]>('/store/orders');

/** GET /store/dashboard */
export const fetchStoreDashboard = (params?: {
  startDate?: string;
  endDate?: string;
}) => apiClient.get<StoreDashboardDTO>('/store/dashboard', { params });
